import { Box, Button, CircularProgress, Link as MuiLink, Modal, Paper, Typography } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import useSnackbar from '../../hooks/useSnackbar';
import { createRelatedStory, deleteRelatedStory, getRelatedStories, updateRelatedStory } from '../../services/api/RelatedStory';
import { getStoryById } from '../../services/api/Story';
import settings from '../../services/settings';
import { IRelatedStoriesData, IStory, RelatedStoryType } from '../../services/types';
import { getCoverUrl } from '../../services/utils';
import { RouteNames } from '../config';
import StorySelectModal from '../../components/StorySelectModal';
import AddRelatedStory from './AddRelatedStoryView';
import RelatedStorySegment from './RelatedStorySegment';
import useStyles from './styles';

interface IProps {}
interface IParamProps {
  storyId: string;
}

const RelatedStory = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const match = useRouteMatch<IParamProps>(`${RouteNames.RelatedStory}/v/:storyId`);
  const storyId = match?.params?.storyId;

  // uses
  const snackbar = useSnackbar();
  const confirm = useConfirmDialog();

  // states
  const [isStoryModalOpen, setIsStoryModalOpen] = useState<boolean>(false);
  const [isRelatedStoryModalOpen, setIsRelatedStoryModalOpen] = useState<boolean>(false);
  const [targetStory, setTargetStory] = useState<IStory>();
  const [currentRelatedStory, setCurrentRelatedStory] = useState<IStory | undefined>();
  const [storyCoverList, setStoryCoverList] = useState<IRelatedStoriesData | undefined>();
  const [episodeEndList, setEpisodeEndList] = useState<IRelatedStoriesData | undefined>();
  const [paywallList, setPaywallList] = useState<IRelatedStoriesData | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // memos
  const coverUrl = useMemo(() => (targetStory ? getCoverUrl(targetStory, 'h') : null), [targetStory]);

  // handlers
  const onSelectStory = (story: IStory) => {
    setIsStoryModalOpen(false);
    setTargetStory(story);
  };
  const onSelectRelatedStory = (story: IStory) => {
    setCurrentRelatedStory(story);
    setIsRelatedStoryModalOpen(false);
  };
  const onClickAddRelatedStory = useCallback(() => {
    setIsRelatedStoryModalOpen(true);
  }, []);
  const onAddRelatedStory = (storyId: number, position: RelatedStoryType, segmentId: number, priority: number) => {
    addRelatedStory(storyId, position, segmentId, priority);
  };
  const onDeleteMapper = (mapperId: number) => {
    confirm.open({
      title: 'Confirm',
      text: 'Are you sure to delete this related story?',
      onProceed: () => {
        deleteMapper(mapperId);
      },
    });
  };
  const onChangePriority = (mapperId: number, priority: number) => {
    updateMapper(mapperId, { priority });
  };

  // watches
  useEffect(() => {
    if (targetStory !== undefined) {
      fetchRelatedStories(targetStory.id);
    }
  }, [targetStory]);

  // APIs
  const fetchRelatedStories = async (storyId: number) => {
    setIsLoading(true);
    try {
      const result = await getRelatedStories(storyId);
      const storiesMap = result.data.reduce((acc: any, cur) => {
        if (!acc.hasOwnProperty(cur.type)) {
          acc[cur.type] = {};
        }
        if (!acc[cur.type].hasOwnProperty(cur.segmentId)) {
          acc[cur.type][cur.segmentId] = { segment: cur.segment, relatedStories: [] };
        }
        acc[cur.type][cur.segmentId].relatedStories.push(cur);
        return acc;
      }, {});

      setStoryCoverList(storiesMap[RelatedStoryType.StoryCover]);
      setEpisodeEndList(storiesMap[RelatedStoryType.Episode]);
      setPaywallList(storiesMap[RelatedStoryType.Paywall]);
    } catch (e) {
      snackbar.error(e);
    } finally {
      setIsLoading(false);
    }
  };
  const addRelatedStory = async (storyId: number, position: RelatedStoryType, segmentId: number, priority: number) => {
    try {
      const result = await createRelatedStory(targetStory!.id, {
        type: position,
        segmentId,
        relatedStoryId: storyId,
        priority,
      });
      setCurrentRelatedStory(undefined);
      fetchRelatedStories(targetStory!.id);
    } catch (e) {
      snackbar.error(e);
    }
  };
  const deleteMapper = async (mapperId: number) => {
    try {
      const result = await deleteRelatedStory(mapperId);
      fetchRelatedStories(targetStory!.id);
    } catch (e) {
      snackbar.error(e);
    }
  };
  const updateMapper = async (mapperId: number, data: any) => {
    try {
      const result = await updateRelatedStory(mapperId, data);
      fetchRelatedStories(targetStory!.id);
    } catch (e) {
      snackbar.error(e);
    }
  };
  const getStory = async (storyId: number) => {
    try {
      const result = await getStoryById(storyId);
      if (result.data) {
        setTargetStory(result.data);
      }
    } catch (e) {
      snackbar.error(e);
    }
  };

  // init
  useEffect(() => {
    if (storyId) {
      getStory(Number(storyId));
    }
  }, []);

  return (
    <div className={classes.root}>
      <Box display="flex" alignContent="space-between" alignItems="center" marginBottom={1}>
        <Typography variant="h5" component="h2" className={classes.topTitle}>
          Related Stories
        </Typography>
        <Button onClick={() => setIsStoryModalOpen(true)} color="primary" variant="contained">
          Select Story
        </Button>
      </Box>
      {targetStory && (
        <div>
          <Paper className={classes.root}>
            <Box className={classes.storyInfoBox}>
              {coverUrl && (
                <div
                  style={{
                    backgroundImage: `url(${coverUrl}?w=320)`,
                  }}
                  className={classes.coverImage}
                />
              )}
              <div className={classes.storyDetailBox}>
                <div>
                  <span className={classes.storyLabel}>ID</span>
                  <span className={classes.segmentName}>{targetStory.id}</span>
                </div>
                <div>
                  <span className={classes.storyLabel}>Title</span>
                  <MuiLink href={`${settings.getCrewURI()}/stories/${targetStory.id}`} target="_blank" className={classes.segmentName}>
                    {targetStory.title}
                  </MuiLink>
                </div>
                <div>
                  <span className={classes.storyLabel}>Pen Name</span>
                  <MuiLink href={`${RouteNames.Penname}/v/${targetStory.penname.id}`} target="_blank" className={classes.segmentName}>
                    {targetStory.penname.name}
                  </MuiLink>
                </div>
              </div>
            </Box>
          </Paper>
          {isLoading && (
            <Box padding={2} textAlign="center">
              <CircularProgress size={30} />
            </Box>
          )}
          {!isLoading && (
            <Box>
              <Box className={classes.addButtonContainer}>
                <Button onClick={onClickAddRelatedStory} color="primary" variant="contained">
                  Add Related Story
                </Button>
              </Box>
              <Box className={classes.mapperTypeContainer}>
                <Typography variant="h6" component="h3">
                  Story Cover
                </Typography>
                <RelatedStorySegment data={storyCoverList} onDeleteMapper={onDeleteMapper} onChangePriority={onChangePriority} />
              </Box>
              <Box className={classes.mapperTypeContainer}>
                <Typography variant="h6" component="h3">
                  Episode End
                </Typography>
                <RelatedStorySegment data={episodeEndList} onDeleteMapper={onDeleteMapper} onChangePriority={onChangePriority} />
              </Box>
              <Box className={classes.mapperTypeContainer}>
                <Typography variant="h6" component="h3">
                  Paywall
                </Typography>
                <RelatedStorySegment data={paywallList} onDeleteMapper={onDeleteMapper} onChangePriority={onChangePriority} />
              </Box>
            </Box>
          )}
        </div>
      )}
      <StorySelectModal
        title={'Select Story'}
        open={isStoryModalOpen}
        onClick={onSelectStory}
        onClose={() => setIsStoryModalOpen(false)}
        filter={'story'}
      />
      <StorySelectModal
        title={'Select Related Story'}
        open={isRelatedStoryModalOpen}
        onClick={onSelectRelatedStory}
        onClose={() => setIsRelatedStoryModalOpen(false)}
        filter={'story'}
      />
      {currentRelatedStory && (
        <Modal
          open={true}
          onEscapeKeyDown={() => {
            setCurrentRelatedStory(undefined);
          }}
        >
          <div className={classes.modalWrap}>
            <AddRelatedStory story={currentRelatedStory} onCancel={() => setCurrentRelatedStory(undefined)} onAdd={onAddRelatedStory} />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default RelatedStory;
